
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BannerTextLine } from '@/types/banner';
import { Button } from '@/components/ui/button';
import { Trash } from 'lucide-react';
import AnimatedImageUploader from './AnimatedImageUploader';

interface BannerContentInputProps {
  textLines: BannerTextLine[];
  onTextLinesChange: (textLines: BannerTextLine[]) => void;
  backgroundColor: string;
  onBackgroundColorChange: (color: string) => void;
  onImageUpload: (file: File) => void;
  borderStyle: string;
  onBorderStyleChange: (style: string) => void;
  borderWidth: number;
  onBorderWidthChange: (width: number) => void;
  borderColor: string;
  onBorderColorChange: (color: string) => void;
  imageWidth: number;
  onImageWidthChange: (width: number) => void;
  imageOpacity: number;
  onImageOpacityChange: (opacity: number) => void;
  isAnimatedGif?: boolean;
  onAnimatedImageUpload?: (file: File, isAnimated: boolean) => void;
}

const BannerContentInput: React.FC<BannerContentInputProps> = ({
  textLines,
  onTextLinesChange,
  backgroundColor,
  onBackgroundColorChange,
  onImageUpload,
  borderStyle,
  onBorderStyleChange,
  borderWidth,
  onBorderWidthChange,
  borderColor,
  onBorderColorChange,
  imageWidth,
  onImageWidthChange,
  imageOpacity,
  onImageOpacityChange,
  isAnimatedGif,
  onAnimatedImageUpload
}) => {
  const [showingAnimatedUploader, setShowingAnimatedUploader] = useState(false);
  
  const updateTextLine = (index: number, field: keyof BannerTextLine, value: any) => {
    const updatedLines = [...textLines];
    updatedLines[index] = {
      ...updatedLines[index],
      [field]: value
    };
    onTextLinesChange(updatedLines);
  };
  
  const addTextLine = () => {
    onTextLinesChange([
      ...textLines,
      {
        text: '',
        fontFamily: 'Arial',
        fontSize: 24,
        color: '#000000',
        bold: false,
        italic: false,
        position: { x: 50, y: 50 + textLines.length * 10 }
      }
    ]);
  };
  
  const removeTextLine = (index: number) => {
    const updatedLines = [...textLines];
    updatedLines.splice(index, 1);
    onTextLinesChange(updatedLines);
  };
  
  const handleImageUpload = (file: File, imageUrl: string, isAnimated: boolean) => {
    onImageUpload(file);
    
    // If we have a handler for animated images, call it
    if (onAnimatedImageUpload) {
      onAnimatedImageUpload(file, isAnimated);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Background Color */}
      <div>
        <Label htmlFor="background-color">Background Color</Label>
        <div className="flex items-center space-x-2 mt-1">
          <div 
            className="h-8 w-8 rounded border"
            style={{ backgroundColor }}
          ></div>
          <Input
            id="background-color"
            type="color"
            value={backgroundColor}
            onChange={(e) => onBackgroundColorChange(e.target.value)}
            className="w-12 h-10 p-1"
          />
          <Input
            type="text"
            value={backgroundColor}
            onChange={(e) => onBackgroundColorChange(e.target.value)}
            className="w-32"
          />
        </div>
      </div>

      {/* Text Lines */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Banner Text</Label>
          <Button 
            type="button" 
            size="sm" 
            variant="outline" 
            onClick={addTextLine}
          >
            Add Text Line
          </Button>
        </div>
        
        {textLines.map((line, index) => (
          <div key={index} className="space-y-3 p-3 border border-gray-200 rounded-md">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Text Line {index + 1}</h4>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0" 
                onClick={() => removeTextLine(index)}
              >
                <Trash className="h-4 w-4" />
              </Button>
            </div>
            
            <div>
              <Label htmlFor={`text-line-${index}`} className="text-xs">Text Content</Label>
              <Textarea
                id={`text-line-${index}`}
                value={line.text}
                onChange={(e) => updateTextLine(index, 'text', e.target.value)}
                className="mt-1"
                placeholder="Enter text for your banner"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor={`font-family-${index}`} className="text-xs">Font Family</Label>
                <Select
                  value={line.fontFamily || 'Arial'}
                  onValueChange={(value) => updateTextLine(index, 'fontFamily', value)}
                >
                  <SelectTrigger id={`font-family-${index}`}>
                    <SelectValue placeholder="Select font" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Arial">Arial</SelectItem>
                    <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                    <SelectItem value="Georgia">Georgia</SelectItem>
                    <SelectItem value="Verdana">Verdana</SelectItem>
                    <SelectItem value="Courier New">Courier New</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor={`font-size-${index}`} className="text-xs">Font Size: {line.fontSize}px</Label>
                <Slider
                  id={`font-size-${index}`}
                  min={10}
                  max={72}
                  step={1}
                  value={[line.fontSize || 24]}
                  onValueChange={(value) => updateTextLine(index, 'fontSize', value[0])}
                  className="mt-2"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Label htmlFor={`text-color-${index}`} className="text-xs">Text Color</Label>
              <div 
                className="h-6 w-6 rounded border"
                style={{ backgroundColor: line.color }}
              ></div>
              <Input
                id={`text-color-${index}`}
                type="color"
                value={line.color || '#000000'}
                onChange={(e) => updateTextLine(index, 'color', e.target.value)}
                className="w-12 h-8 p-1"
              />
            </div>
            
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={line.bold || false}
                  onChange={(e) => updateTextLine(index, 'bold', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium">Bold</span>
              </label>
              
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={line.italic || false}
                  onChange={(e) => updateTextLine(index, 'italic', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium">Italic</span>
              </label>
            </div>
          </div>
        ))}
        
        {textLines.length === 0 && (
          <p className="text-gray-500 text-center py-3">
            No text lines added. Click "Add Text Line" to add content.
          </p>
        )}
      </div>

      {/* Image Upload */}
      <div className="space-y-3">
        {showingAnimatedUploader ? (
          <AnimatedImageUploader onImageUploaded={handleImageUpload} />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <Label htmlFor="image-upload">Banner Image</Label>
              <Button 
                variant="link" 
                size="sm" 
                onClick={() => setShowingAnimatedUploader(true)}
                className="text-blue-600 h-6 p-0"
              >
                Use Animated GIF/PSD Uploader
              </Button>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center mt-1">
              <input
                type="file"
                id="image-upload"
                className="hidden"
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && onImageUpload(e.target.files[0])}
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <div className="flex flex-col items-center justify-center py-4">
                  <div className="h-10 w-10 text-gray-400 mb-2">
                    📷
                  </div>
                  <p className="text-sm text-gray-600 font-medium">
                    Click to upload an image
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, GIF up to 5MB
                  </p>
                </div>
              </label>
            </div>
          </>
        )}
      </div>

      {/* Image Settings */}
      <div>
        <h3 className="font-medium mb-2">Image Settings</h3>
        
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="image-width">Image Width: {imageWidth}%</Label>
            </div>
            <Slider
              id="image-width"
              min={10}
              max={100}
              step={1}
              value={[imageWidth]}
              onValueChange={(value) => onImageWidthChange(value[0])}
              className="mt-2"
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="image-opacity">Opacity: {imageOpacity}%</Label>
            </div>
            <Slider
              id="image-opacity"
              min={10}
              max={100}
              step={1}
              value={[imageOpacity]}
              onValueChange={(value) => onImageOpacityChange(value[0])}
              className="mt-2"
            />
          </div>
        </div>
      </div>

      {/* Border Settings */}
      <div>
        <h3 className="font-medium mb-2">Border Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="border-style">Border Style</Label>
            <Select value={borderStyle} onValueChange={onBorderStyleChange}>
              <SelectTrigger id="border-style">
                <SelectValue placeholder="Select style" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="solid">Solid</SelectItem>
                <SelectItem value="dashed">Dashed</SelectItem>
                <SelectItem value="dotted">Dotted</SelectItem>
                <SelectItem value="double">Double</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="border-width">Border Width: {borderWidth}px</Label>
            <Slider
              id="border-width"
              min={1}
              max={10}
              step={1}
              value={[borderWidth]}
              onValueChange={(value) => onBorderWidthChange(value[0])}
              className="mt-2"
              disabled={borderStyle === "none"}
            />
          </div>
          
          <div className="md:col-span-2">
            <Label htmlFor="border-color">Border Color</Label>
            <div className="flex items-center space-x-2 mt-1">
              <div 
                className="h-8 w-8 rounded border"
                style={{ backgroundColor: borderColor }}
              ></div>
              <Input
                id="border-color"
                type="color"
                value={borderColor}
                onChange={(e) => onBorderColorChange(e.target.value)}
                className="w-12 h-10 p-1"
                disabled={borderStyle === "none"}
              />
              <Input
                type="text"
                value={borderColor}
                onChange={(e) => onBorderColorChange(e.target.value)}
                className="w-32"
                disabled={borderStyle === "none"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerContentInput;
